<template>
  <div
    v-bind="getAttributes()"
    :class="currentClass"
    :style="styles"
    @click="onClick"
    @onFocus="onFocus"
    @blur="onBlur"
  >
    <o-icon
      v-if="icon && !iconAfter"
      :icon="icon"
      :size="13"
      :class="leftIconClass"
      v-on="handlers"
    />
    <slot />
    <o-icon
      v-if="icon && iconAfter"
      :icon="icon"
      :size="13"
      :class="rightIconClass"
      v-on="handlers"
    />
  </div>
</template>

<script lang="ts">
export default { name: 'OChip' }
</script>

<script setup lang="ts">
interface Props {
  id?: string | null
  autofocus?: boolean | null
  disabled?: boolean
  name?: string | null
  tagName?: string
  size?: string | null
  icon?: string | string[] | null
  iconAfter?: boolean
  translucent?: boolean
  transparent?: boolean
  hover?: boolean
  square?: boolean
  colour?: string | null
  variant?: string | null
  flat?: boolean
  outline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  autofocus: null,
  disabled: false,
  name: null,
  tagName: 'button',
  size: null,
  icon: null,
  iconAfter: false,
  translucent: false,
  transparent: false,
  hover: false,
  square: true,
  colour: null,
  variant: null,
  flat: false,
  outline: false,
})

const emit = defineEmits<{
  (e: 'blur', event: Event): void
  (e: 'focus', event: Event): void
  (e: 'click', event: Event): void
  (e: 'action', event: Event): void
}>()

const attrs = useAttrs()

// Colour classes
const defaultColorClass = 'text-gray-700 bg-gray-200 dark:bg-gray-800 dark:text-white/80 hover:bg-gray-300 dark:border-gray-900'
const infoColorClass = 'text-white bg-blue-600 dark:bg-blue-800 border-blue-400 hover:bg-blue-700'
const successColorClass = 'text-white bg-green-500 dark:bg-green-700 border-green-400 hover:bg-green-700'
const dangerColorClass = 'text-white bg-red-500 dark:bg-red-700 border-red-500 hover:bg-red-600 hover:border-red-600'
const warningColorClass = 'text-white bg-orange-500 dark:bg-orange-700 border-orange-500 hover:bg-orange-600 hover:border-orange-600'
const lightColorClass = 'text-white bg-gray-100 hover:bg-gray-200 hover:text-gray-700'
const darkColorClass = 'text-white bg-gray-700 hover:bg-gray-700'
const primaryColorClass = 'text-white bg-primary-600 dark:bg-primary-800 hover:bg-primary-600/85'

const defaultFlatClass = 'text-gray-600 bg-gray-100 dark:bg-white/5 dark:text-white/80 hover:bg-gray-200 hover:text-gray-700'
const infoFlatClass = 'bg-blue-200 dark:bg-blue-800/20 text-blue-700 dark:text-blue-500 hover:bg-blue-600 hover:text-white'
const successFlatClass = 'bg-green-100 dark:bg-green-800/20 text-green-700 dark:text-green-500 hover:bg-green-600 hover:text-white'
const dangerFlatClass = 'bg-red-200 dark:bg-red-800/20 text-red-700 dark:text-red-500 hover:bg-red-600 hover:text-white'
const warningFlatClass = 'bg-orange-100 dark:bg-orange-800/20 text-orange-700 dark:text-orange-500 hover:bg-orange-600 hover:text-white'
const lightFlatClass = 'bg-gray-100 dark:bg-blue-800/20 text-gray-700 dark:text-gray-200 hover:bg-gray-200 hover:text-gray-700'
const darkFlatClass = 'bg-gray-100 dark:bg-blue-800/20 text-gray-700 dark:text-gray-200 hover:bg-gray-600 hover:text-white'
const primaryFlatClass = 'bg-primary-200 dark:bg-primary-800/20 text-primary-700 dark:text-primary-500 hover:bg-primary-600 hover:text-white'

const defaultOutlineClass = 'border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-white dark:text-gray-300'
const infoOutlineClass = 'border border-blue-400 dark:border-blue-800 text-blue-600'
const successOutlineClass = 'border border-green-400 dark:border-green-800 text-green-600'
const dangerOutlineClass = 'border border-red-400 dark:border-red-800 text-red-600'
const warningOutlineClass = 'border border-orange-400 dark:border-orange-800 text-orange-500'
const lightOutlineClass = 'border border-gray-50 dark:border-gray-50 text-gray-50'
const darkOutlineClass = 'border border-gray-800 dark:border-gray-800 text-gray-800'
const primaryOutlineClass = 'border border-primary-400 dark:border-primary-800 text-primary-600 dark:text-primary-400'

function variantsHelper(classes: string[]) {
  if (!props.flat && !props.outline) {
    switch (props.variant) {
      case 'info': classes.push(infoColorClass); break
      case 'danger': classes.push(dangerColorClass); break
      case 'warning': classes.push(warningColorClass); break
      case 'success': classes.push(successColorClass); break
      case 'light': classes.push(lightColorClass); break
      case 'dark': classes.push(darkColorClass); break
      case 'primary': classes.push(primaryColorClass); break
      default: classes.push(defaultColorClass); break
    }
  }
  if (props.flat) {
    switch (props.variant) {
      case 'info': classes.push(infoFlatClass); break
      case 'danger': classes.push(dangerFlatClass); break
      case 'warning': classes.push(warningFlatClass); break
      case 'success': classes.push(successFlatClass); break
      case 'light': classes.push(lightFlatClass); break
      case 'dark': classes.push(darkFlatClass); break
      case 'primary': classes.push(primaryFlatClass); break
      default: classes.push(defaultFlatClass); break
    }
  }
  if (props.outline) {
    switch (props.variant) {
      case 'info': classes.push(infoOutlineClass); break
      case 'danger': classes.push(dangerOutlineClass); break
      case 'warning': classes.push(warningOutlineClass); break
      case 'success': classes.push(successOutlineClass); break
      case 'light': classes.push(lightOutlineClass); break
      case 'dark': classes.push(darkOutlineClass); break
      case 'primary': classes.push(primaryOutlineClass); break
      default: classes.push(defaultOutlineClass); break
    }
  }
  return classes
}

const baseClass = 'flex flex-row items-center inline-flex items-center justify-center transition ease-in-out duration-300 leading-none'
const disabledClass = 'opacity-50 pointer-events-none'
const translucentCls = '/10'
const transparentCls = '/0'
const defaultSizeClass = 'px-3 py-1 min-h-6 text-xs font-medium'
const largeSizeClass = 'px-3 h-7 text-sm font-medium'
const smallSizeClass = 'px-2 py-1 min-h-6 text-xs font-medium'
const xsSizeClass = 'px-2 h-5 text-xs font-medium'
const xxsSizeClass = 'px-1 h-4 text-2xs'
const roundedClass = 'rounded-full'
const squareClass = 'rounded'

const slots = useSlots()
const instance = getCurrentInstance()
const hasClickListener = computed(() => !!(instance?.vnode?.props?.onClick))
const hasActionListener = computed(() => !!(instance?.vnode?.props?.onAction))
const needsContrastingActionIcon = computed(() => props.flat || props.outline || !props.variant)

const actionIconClass = computed(() => {
  if (!hasActionListener.value) {
    return ''
  }

  return needsContrastingActionIcon.value
    ? 'bg-black/15 hover:bg-black/25 dark:bg-white/20 dark:hover:bg-white/30 transition duration-300'
    : 'bg-white/25 hover:bg-white/50 transition duration-300'
})

const leftIconClass = computed(() => [
  hasActionListener.value ? `${actionIconClass.value} ml-2px` : (slots.default ? '-ml-1' : ''),
  'cursor-pointer pointer-events-auto p-2px rounded',
  slots.default ? 'md:mr-2' : '-mx-1',
])

const rightIconClass = computed(() => [
  hasActionListener.value ? actionIconClass.value : (slots.default ? '-mr-1' : ''),
  'cursor-pointer pointer-events-auto p-2px rounded',
  slots.default ? 'md:ml-2' : '-mx-1',
])

const currentClass = computed(() => {
  const classes = ['o-chip', baseClass]
  if (props.disabled) {
    classes.push(disabledClass)
  }
  if (props.translucent) {
    classes.push(translucentCls)
  }
  if (props.transparent) {
    classes.push(transparentCls)
  }

  if (props.size === null) {
    classes.push(defaultSizeClass)
  } else if (props.size === 'sm') {
    classes.push(smallSizeClass)
  } else if (props.size === 'lg') {
    classes.push(largeSizeClass)
  } else if (props.size === 'xs') {
    classes.push(xsSizeClass)
  } else if (props.size === '2xs') {
    classes.push(xxsSizeClass)
  }

  if (props.square) {
    classes.push(squareClass)
  } else {
    classes.push(roundedClass)
  }

  if (!hasClickListener.value && !props.hover) {
    classes.push('pointer-events-none')
  }

  if (props.hover) {
    classes.push('cursor-pointer')
  }

  return variantsHelper(classes)
})

const styles = computed(() => {
  const s: Record<string, string> = {}
  if (props.colour) {
    s.backgroundColor = props.flat ? `#${props.colour}25 !important` : `#${props.colour} !important`
    s.color = props.flat ? `#${props.colour} !important` : '#fff !important'
  }
  return s
})

const handlers = computed(() => {
  if (hasActionListener.value) {
    return {
      click(e: Event) {
        e.stopPropagation()
        onAction(e)
      },
    }
  }
  return {}
})

function onBlur(e: Event) {
  emit('blur', e)
}

function onFocus(e: Event) {
  emit('focus', e)
}

function onClick(e: Event) {
  emit('click', e)
}

function onAction(e: Event) {
  emit('action', e)
}

function getAttributes() {
  return {
    id: props.id,
    autofocus: props.autofocus,
    disabled: props.disabled,
    name: props.name,
  }
}
</script>
